import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { curriculumAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LearnerNav from '../components/nav/LearnerNav';
import MasteryBadgePanel from '../components/adaptive/MasteryBadgePanel';
import FlashcardReviewSession from '../components/scenes/FlashcardReviewSession';

const DOMAIN_ICONS = {
  football: '🏈',
  ai: '🤖',
  ai_orchestrator: '🤖',
  ai_entrepreneur: '🚀',
  ai_beginner: '🧠',
  lsat: '⚖️',
  sat: '📐',
};

const TRACK_LABELS = {
  ai_fundamentals: 'AI Fundamentals',
  ai_orchestrator_certificate: 'AI Orchestrator Certificate',
  ai_entrepreneur_certificate: 'AI Entrepreneur Certificate',
};

/** Convert API completion_pct (0–1 fraction OR 0–100 integer) to a display percentage. */
function displayCompletionPct(raw) {
  const n = Number(raw);
  if (raw == null || Number.isNaN(n) || n < 0) return 0;
  // Values like 100 mean percent; values ≤ 1 mean fractions (0.85 → 85%).
  if (n > 1) return Math.min(100, Math.round(n));
  return Math.min(100, Math.round(n * 100));
}

/** Return a progress bar color based on completion percentage. */
function progressBarColor(pct) {
  if (pct <= 30) return '#f59e0b';  // amber-warning
  if (pct <= 80) return '#FFCF99';  // teachiq amber
  return '#92140C';                  // teachiq crimson
}

function formatDate(isoString) {
  if (!isoString) return 'Never';
  try {
    return new Date(isoString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'Unknown';
  }
}

export default function ProgressDashboardPage() {
  const { t } = useTranslation();
  const { learner } = useAuth();
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Review session overlay state
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewDomain, setReviewDomain] = useState(null); // null = all due cards

  const handleStartReview = useCallback((domain = null) => {
    setReviewDomain(domain);
    setReviewOpen(true);
  }, []);

  const handleExitReview = useCallback(() => {
    setReviewOpen(false);
    setReviewDomain(null);
  }, []);

  useEffect(() => {
    curriculumAPI
      .getProgressSummary()
      .then(({ data }) => setDomains(data.domains || []))
      .catch(() => setError('Could not load progress data. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const hasProgress = domains.some((d) => d.modules_completed > 0 || d.lessons_completed > 0);

  return (
    <div style={{ minHeight: '100vh', background: '#FFF8F0' }}>
      <LearnerNav />
      <div className="dashboard-page">
      <main className="dashboard-content">
        <h2 style={{ marginBottom: '8px', color: 'var(--teachiq-dark, #1E1E24)' }}>My Progress</h2>
        <p style={{ color: '#6a6a72', marginBottom: '28px', fontSize: '0.95rem' }}>
          Your learning journey across all enrolled domains.
        </p>

        {loading && (
          <div className="page-loading" style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="progress-spinner" />
            <p style={{ marginTop: '16px', color: '#6a6a72' }}>Loading your progress…</p>
          </div>
        )}

        {!loading && error && (
          <div className="auth-error">{error}</div>
        )}

        {!loading && !error && !hasProgress && (
          <div className="progress-empty-state">
            <span className="progress-empty-icon">📚</span>
            <h3>No progress yet</h3>
            <p>Start a module to begin tracking your learning journey.</p>
            <button className="btn-primary" style={{ width: 'auto', marginTop: '16px', padding: '10px 28px' }} onClick={() => navigate('/dashboard')}>
              Browse Domains
            </button>
          </div>
        )}

        {/* Adaptive mastery badges + review launcher */}
        {!loading && !error && learner?.learner_id && (
          <MasteryBadgePanel
            learnerId={learner.learner_id}
            onStartReview={() => handleStartReview(null)}
          />
        )}

        {!loading && !error && (
          <div className="progress-domain-grid">
            {domains.flatMap((domain) => {
              // AI consolidation: show per-track cards (8–9 modules), not domain-wide 26.
              const trackCards = (domain.per_track_breakdown || []).length > 0
                ? domain.per_track_breakdown.map((track) => ({
                    key: `${domain.domain_id}:${track.track_id}`,
                    domain_id: domain.domain_id,
                    track_id: track.track_id,
                    label: track.display_name || TRACK_LABELS[track.track_id] || track.track_id,
                    modules_completed: track.modules_completed || 0,
                    modules_total: track.modules_total || 0,
                    completion_pct: track.completion_pct,
                    lessons_completed: null,
                    lessons_total: 0,
                    last_active: domain.last_active,
                  }))
                : [{
                    key: domain.domain_id,
                    domain_id: domain.domain_id,
                    track_id: null,
                    label: domain.domain_label,
                    modules_completed: domain.modules_completed || 0,
                    modules_total: domain.modules_total || 0,
                    completion_pct: domain.completion_pct,
                    lessons_completed: domain.lessons_completed,
                    lessons_total: domain.lessons_total || 0,
                    last_active: domain.last_active,
                  }];

              return trackCards.map((card) => {
              const icon = DOMAIN_ICONS[domain.domain_id] || '📖';
              const pct = displayCompletionPct(card.completion_pct);
              const hasAnyProgress = card.modules_completed > 0 || (card.lessons_completed || 0) > 0;
              const barColor = progressBarColor(pct);
              const isFullyComplete = pct >= 100;
              const domainPath = card.track_id
                ? `/domain/${card.domain_id}?track=${card.track_id}`
                : `/domain/${card.domain_id}`;

              return (
                <div key={card.key} className="progress-domain-card">
                  <div className="progress-domain-header">
                    <span className="progress-domain-icon">{icon}</span>
                    <div style={{ flex: 1 }}>
                      <h3 className="progress-domain-name">{card.label}</h3>
                      <span className="progress-domain-pct">{pct}% complete</span>
                    </div>
                    {isFullyComplete && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: '#92140C', color: '#fff',
                        borderRadius: 9999, padding: '3px 10px',
                        fontSize: 11, fontWeight: 700, flexShrink: 0,
                      }}>
                        ✓ Completed
                      </span>
                    )}
                  </div>

                  <div className="progress-bar-track">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${pct}%`, background: barColor }}
                      role="progressbar"
                      aria-valuenow={pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>

                  <div className="progress-stats-row">
                    <span className="progress-stat">
                      <strong>{card.modules_completed}</strong> / {card.modules_total} Modules Completed
                    </span>
                    {card.lessons_total > 0 && card.lessons_completed != null && (
                      <span className="progress-stat">
                        <strong>{card.lessons_completed}</strong> / {card.lessons_total} Lessons
                      </span>
                    )}
                    {card.last_active && (
                      <span className="progress-stat progress-stat-last-active">
                        Last active: {formatDate(card.last_active)}
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                    {hasAnyProgress && (
                      <button
                        className="btn-small"
                        onClick={() => navigate(domainPath)}
                      >
                        {isFullyComplete ? 'Review Curriculum' : 'Continue Learning'}
                      </button>
                    )}
                    {hasAnyProgress && (
                      <button
                        className="btn-small"
                        data-testid={`review-btn-${card.key}`}
                        style={{
                          background: 'rgba(255,207,153,0.15)',
                          border: '1px solid #FFCF99',
                          color: '#92140C',
                        }}
                        onClick={() => handleStartReview(card.domain_id)}
                      >
                        Review Cards
                      </button>
                    )}
                    {isFullyComplete && (
                      <button
                        className="btn-small"
                        style={{
                          background: '#1E1E24',
                          color: '#FFF8F0',
                          border: '1px solid #1E1E24',
                        }}
                        onClick={() => navigate(`/dashboard/certificate/${card.domain_id}`)}
                      >
                        🎓 View Certificate
                      </button>
                    )}
                  </div>
                </div>
              );
              });
            })}
          </div>
        )}
      </main>

      {/* ── Flashcard Review Session overlay ────────────────────────────────── */}
      {reviewOpen && learner?.learner_id && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Flashcard Review Session"
          data-testid="review-session-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(10,14,26,0.96)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 16px',
          }}
        >
          {/* Close button */}
          <button
            aria-label="Close review session"
            data-testid="review-session-close"
            onClick={handleExitReview}
            style={{
              position: 'absolute',
              top: 16,
              right: 20,
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              fontSize: 28,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ×
          </button>

          {reviewDomain && (
            <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>
              {reviewDomain.replace(/_/g, ' ')} — Review Session
            </p>
          )}

          <div style={{ width: '100%', maxWidth: 640 }}>
            <FlashcardReviewSession
              learnerId={learner.learner_id}
              domain={reviewDomain || undefined}
              onExit={handleExitReview}
            />
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
